import apiClient from './apiClient'; // Import the centralized apiClient
import { computeDivHrFromHistory } from '../utils/divHr';

// Define the response types based on backend DTOs
export interface CharacterCountStats {
  totalCharacters: number;
  game: string;
}

export interface DivineOrbCountStats {
  divineOrbCount: number;
  game: string;
}

export interface ActiveAlertCountStats {
  activeAlertCount: number;
  game?: string;
}

export interface ActiveBotsCountStats {
  activeCount: number;
}

export interface DivineHistoryDashboardPoint {
  timestamp: Date;
  cumulativeDivineOrbs: number;
}

export interface DivineHistoryDashboardStats {
  historyPoints: DivineHistoryDashboardPoint[];
  lastHourGain: number;
  currentTotal: number;
  game: string;
  league: string;
}

export interface DivHrDashboardPoint {
  timestamp: Date;
  divPerHour: number;
  divineOrbsGained: number;
}

export interface DivHrDashboardStats {
  historyPoints: DivHrDashboardPoint[];
  currentDivPerHour: number;
  totalDivineOrbsGained24h: number;
  game: string;
  league: string;
}

export interface AlertMetricsHistoryPoint {
  snapshotTime: Date;
  lowCount: number;
  mediumCount: number;
  highCount: number;
  criticalCount: number;
  totalCount: number;
}

export interface AlertMetricsHistoryStats {
  historyPoints: AlertMetricsHistoryPoint[];
}

// Consolidated stats type for the dashboard
export interface DashboardStats {
  totalCharacters: number;
  divineOrbCount: number;
  activeAlertCount: number;
  activeBotsCount: number;
}

/**
 * Fetches character count statistics for a specified game
 * @param game - Optional game identifier (PoE1 or PoE2)
 */
const getCharacterCount = async (): Promise<CharacterCountStats> => {
  try {
    const response = await apiClient.get<CharacterCountStats>('dashboard/stats/character-count');
    return response.data;
  } catch (error) {
    console.error('Error fetching character count:', error);
    throw error;
  }
};

/**
 * Fetches divine orb count statistics for a specified game
 * @param game - Optional game identifier (PoE1 or PoE2)
 */
const getDivineOrbCount = async (): Promise<DivineOrbCountStats> => {
  try {
    const response = await apiClient.get<DivineOrbCountStats>('dashboard/stats/divine-orb-count');
    return response.data;
  } catch (error) {
    console.error('Error fetching divine orb count:', error);
    throw error;
  }
};

/**
 * Fetches active alert count statistics for a specified game
 * @param game - Optional game identifier (PoE1 or PoE2)
 */
const getActiveAlertCount = async (): Promise<ActiveAlertCountStats> => {
  try {
    const response = await apiClient.get<ActiveAlertCountStats>('dashboard/stats/active-alert-count');
    return response.data;
  } catch (error) {
    console.error('Error fetching active alert count:', error);
    throw error;
  }
};

/**
 * Fetches active bots count statistics
 */
const getActiveBotsCount = async (): Promise<ActiveBotsCountStats> => {
  try {
    const response = await apiClient.get<ActiveBotsCountStats>('/characters/active-count');
    return response.data;
  } catch (error) {
    console.error('Error fetching active bots count:', error);
    throw error;
  }
};

/**
 * Fetches divine orb history dashboard data for the last 24 hours
 * @param game - Optional game identifier (PoE1 or PoE2)
 * @param league - Optional league identifier
 */
const getDivineHistoryDashboard = async (): Promise<DivineHistoryDashboardStats> => {
  try {
    const response = await apiClient.get<DivineHistoryDashboardStats>('dashboard/stats/divine-history');
    // Transform timestamp strings to Date objects
    const transformedData = {
      ...response.data,
      historyPoints: response.data.historyPoints.map(point => ({
        ...point,
        timestamp: new Date(point.timestamp),
      })),
    };

    return transformedData;
  } catch (error) {
    console.error('Error fetching divine history dashboard:', error);
    throw error;
  }
};

/**
 * Fetches all dashboard statistics (characters, divine orbs, active alerts) for a specified game
 * @param game - Optional game identifier (PoE1 or PoE2)
 */
const getAllDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const [charactersData, divineOrbsData, alertsData, activeBotsData] = await Promise.all([
      getCharacterCount(),
      getDivineOrbCount(),
      getActiveAlertCount(),
      getActiveBotsCount(),
    ]);

    return {
      totalCharacters: charactersData.totalCharacters,
      divineOrbCount: divineOrbsData.divineOrbCount,
      activeAlertCount: alertsData.activeAlertCount,
      activeBotsCount: activeBotsData.activeCount,
    };
  } catch (error) {
    console.error('Error fetching all dashboard stats:', error);
    throw error;
  }
};

/**
 * Computes Div/Hr dashboard data for the last 24 hours from divine history
 * @param game - Optional game identifier (PoE1 or PoE2)
 * @param league - Optional league identifier
 */
const getDivHrDashboard = async (): Promise<DivHrDashboardStats> => {
  try {
    // Fetch 15-min divine history instead of dedicated endpoint
    const divineHistory = await getDivineHistoryDashboard();

    // Convert to format expected by computeDivHrFromHistory
    const historyPoints = divineHistory.historyPoints.map(p => ({
      timestamp: p.timestamp,
      cumulativeDivineOrbs: p.cumulativeDivineOrbs,
    }));

    // Compute div/hr rates from the history
    const divHrSeries = computeDivHrFromHistory(historyPoints);

    const currentDivPerHour = divHrSeries.length > 0 ? divHrSeries[divHrSeries.length - 1].divPerHour : 0;

    const totalDivineOrbsGained24h = divHrSeries.reduce((sum, p) => sum + p.divineOrbsGained, 0);

    return {
      historyPoints: divHrSeries,
      currentDivPerHour,
      totalDivineOrbsGained24h,
      game: divineHistory.game,
      league: divineHistory.league,
    };
  } catch (error) {
    console.error('Error computing Div/Hr dashboard:', error);
    throw error;
  }
};

const getAlertMetricsHistory = async (): Promise<AlertMetricsHistoryStats> => {
  try {
    const response = await apiClient.get<AlertMetricsHistoryStats>('dashboard/stats/alert-metrics-history');
    return {
      ...response.data,
      historyPoints: response.data.historyPoints.map(point => ({
        ...point,
        snapshotTime: new Date(point.snapshotTime),
      })),
    };
  } catch (error) {
    console.error('Error fetching alert metrics history:', error);
    throw error;
  }
};

const dashboardService = {
  getCharacterCount,
  getDivineOrbCount,
  getActiveAlertCount,
  getActiveBotsCount,
  getDivineHistoryDashboard,
  getDivHrDashboard,
  getAllDashboardStats,
  getAlertMetricsHistory,
};

export default dashboardService;
